import { DataTable } from "@/components/ui/data-table";
import { User, userColumns } from "@/lib/columns";
import { users } from "@/lib/constants";
// import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import { axiosPrivate } from "@/api/axios";

import React from "react";
import { Row } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import ActionsMenu from "@/components/dashboard/ActionsMenu";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";

import { toast } from "sonner";

function Users() {
  const [data, setData] = React.useState<User[]>(() => []);
  const [isLoading, setIsLoading] = React.useState(true);
  // const axiosPrivate = useAxiosPrivate();

  React.useEffect(() => {
    const getData = async () => {
      try {
        const res = await axiosPrivate.get("/users/");
        if (res?.data?.success) {
          // Filter out admin users - only show regular users and experts
          const filteredUsers = res?.data?.data?.filter((user: User) => 
            user.role !== "admin" && user.role !== "expert"
          );
          setData(filteredUsers);
        } else {
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };
    getData();
  }, []);

  const actions = {
    id: "actions",
    header: "Actions",
    cell: ({ row }: { row: Row<User> }) => {
      async function makeExpert() {
        try {
          const res = await axiosPrivate.put(`/users/${row.original.id}`, {
            role: "expert",
          });
          toast(`User with ID ${row.original.id} is added to Expert`);
          setData((prev) => {
            return prev.map((item) => {
              if (item.id === row.original.id) {
                return { ...item, role: "expert" };
              }
              return item;
            });
          });
        } catch (error) {
          toast.error("Something went wrong on the server side");
        }
      }
      async function removeExpert() {
        try {
          const res = await axiosPrivate.put(`/users/${row.original.id}`, {
            role: "user",
          });
          toast(`User with ID ${row.original.id} is removed from Expert`);
          setData((prev) => {
            return prev.map((item) => {
              if (item.id === row.original.id) {
                return { ...item, role: "user" };
              }
              return item;
            });
          });
        } catch (error) {
          toast.error("Something went wrong on the server side");
        }
      }

      async function handleDelete() {
        try {
          const res = await axiosPrivate.delete(`/users/${row.original.id}`);
          setData((prev) => {
            return prev.filter((item) => item.id !== row.original.id);
          });
          toast(`User with ID ${row.original.id} is deleted.`);
        } catch (error) {
          toast.error("Something went wrong on the server side");
        }
      }
      const confirmActionButtonRef = React.useRef<() => void>(() => {});
      const [openConfirm, setOpenConfirm] = React.useState(false);

      return (
        <>
          <ActionsMenu
            extraItems={[
              ...(row.original.role !== "expert"
                ? [{ label: "Make Expert", onClick: makeExpert }]
                : []),
              ...(row.original.role === "expert"
                ? [{ label: "Remove Expert", onClick: removeExpert }]
                : []),
            ]}
            onDeleteConfirm={(proceed) => {
              confirmActionButtonRef.current = proceed;
              setOpenConfirm(true);
            }}
          />
          <ConfirmDialog
            actionButtonRef={confirmActionButtonRef}
            open={openConfirm}
            onOpenChange={setOpenConfirm}
            isLoading={false}
            AlertdialogDescription={`This will permanently delete user with ID ${row.original.id}. Are you sure?`}
          />
        </>
      );
    },
  };

  const columns = React.useMemo(() => [...userColumns, actions], []);
  return (
    <div className="space-y-4">
      <h1 className="text-3xl">User Management</h1>
      <p>
        Manage all users in one place, Control access, assign roles, and monitor
        activity across your platforms.
      </p>
      {!isLoading && (
        <DataTable
          columns={columns}
          data={data}
          placeholder="Filter Users"
          filterColumns={["gender", "country", "username"]}
        />
      )}
    </div>
  );
}

export default Users;
